import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit3, Trash2, Eye, EyeOff, Star, FileText, Newspaper, Heart, Globe, Clock, RefreshCw, X, Save, ChevronDown, Upload, Image } from 'lucide-react';
import Toggle from '../components/Toggle';
import axios from 'axios';

const API = 'https://api.lifeshare.app';

interface CMSItem {
    id: number;
    content_type: string;
    title: string;
    slug: string;
    excerpt: string;
    body: string;
    category: string;
    cover_image: string;
    author: string;
    read_time: number;
    is_published: boolean;
    is_featured: boolean;
    language: string;
    tags: string;
    meta_description: string;
    view_count: number;
    sort_order: number;
    published_at: string | null;
    created_at: string;
    updated_at: string;
}

interface Stats {
    total: number;
    published: number;
    drafts: number;
    blog: number;
    impact: number;
    press: number;
}

const TYPES = [
    { value: '', label: 'Tümü', icon: FileText, color: 'gray' },
    { value: 'blog', label: 'Blog', icon: FileText, color: 'blue' },
    { value: 'impact', label: 'Etki', icon: Heart, color: 'green' },
    { value: 'press', label: 'Basın', icon: Newspaper, color: 'purple' },
];

const CATEGORIES: Record<string, string[]> = {
    blog: ['Update', 'Features', 'Safety', 'Driving', 'Tips', 'Plans', 'Privacy', 'General'],
    impact: ['Community', 'Safety', 'Environment', 'Innovation', 'General'],
    press: ['Release', 'Announcement', 'Partnership', 'Award', 'General'],
};

const LANGS = [
    { value: 'en', label: '🇬🇧 EN' }, { value: 'tr', label: '🇹🇷 TR' },
    { value: 'de', label: '🇩🇪 DE' }, { value: 'fr', label: '🇫🇷 FR' },
    { value: 'es', label: '🇪🇸 ES' }, { value: 'it', label: '🇮🇹 IT' },
    { value: 'nl', label: '🇳🇱 NL' }, { value: 'ru', label: '🇷🇺 RU' },
    { value: 'ja', label: '🇯🇵 JA' }, { value: 'ko', label: '🇰🇷 KO' },
    { value: 'zh-Hans', label: '🇨🇳 ZH' }, { value: 'hi', label: '🇮🇳 HI' },
    { value: 'id', label: '🇮🇩 ID' }, { value: 'ar', label: '🇸🇦 AR' },
    { value: 'pl', label: '🇵🇱 PL' }, { value: 'pt', label: '🇧🇷 PT' },
    { value: 'vi', label: '🇻🇳 VI' },
];

const StatCard: React.FC<{ value: number; label: string; color: string }> = ({ value, label, color }) => {
    const colors: Record<string, string> = {
        blue: 'from-blue-50 to-blue-100 border-blue-200 text-blue-700',
        green: 'from-green-50 to-green-100 border-green-200 text-green-700',
        purple: 'from-purple-50 to-purple-100 border-purple-200 text-purple-700',
        gray: 'from-gray-50 to-gray-100 border-gray-200 text-gray-700',
        pink: 'from-pink-50 to-pink-100 border-pink-200 text-pink-700',
        amber: 'from-amber-50 to-amber-100 border-amber-200 text-amber-700',
    };
    return (
        <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-3 text-center`}>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-[11px] opacity-60 uppercase tracking-wide">{label}</p>
        </div>
    );
};

const emptyForm = (): Partial<CMSItem> => ({
    content_type: 'blog', title: '', slug: '', excerpt: '', body: '',
    category: 'General', cover_image: '', author: 'LifeShare Team',
    read_time: 5, is_published: false, is_featured: false,
    language: 'en', tags: '', meta_description: '', sort_order: 0,
});

export default function CMSManagement() {
    const [items, setItems] = useState<CMSItem[]>([]);
    const [stats, setStats] = useState<Stats | null>(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [pubFilter, setPubFilter] = useState<string>('');
    const [showEditor, setShowEditor] = useState(false);
    const [editItem, setEditItem] = useState<Partial<CMSItem>>(emptyForm());
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [imageMode, setImageMode] = useState<'url' | 'upload'>('url');

    const fetchData = async () => {
        setLoading(true);
        try {
            const params: Record<string, string> = {};
            if (typeFilter) params.content_type = typeFilter;
            if (pubFilter === 'true') params.is_published = 'true';
            if (pubFilter === 'false') params.is_published = 'false';
            if (search) params.search = search;

            const res = await axios.get(`${API}/api/v1/cms/admin/list`, { params });
            setItems(res.data.items || []);
            setStats(res.data.stats || null);
        } catch (e) {
            console.error('CMS fetch error:', e);
        }
        setLoading(false);
    };

    useEffect(() => { fetchData(); }, [typeFilter, pubFilter]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        fetchData();
    };

    const openCreate = () => {
        setEditItem(emptyForm());
        setImageMode('url');
        setShowEditor(true);
    };

    const openEdit = (item: CMSItem) => {
        setEditItem({ ...item });
        setImageMode(item.cover_image?.startsWith('/uploads') ? 'upload' : 'url');
        setShowEditor(true);
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) return alert('Dosya 5MB\'dan büyük olamaz');
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await axios.post(`${API}/api/v1/cms/admin/upload-image`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const url = `${API}${res.data.url}`;
            setEditItem(prev => ({ ...prev, cover_image: url }));
        } catch (err: any) {
            alert(err.response?.data?.detail || 'Yükleme hatası');
        }
        setUploading(false);
    };

    const handleSave = async () => {
        if (!editItem.title?.trim()) return alert('Başlık gerekli');
        setSaving(true);
        try {
            if (editItem.id) {
                await axios.put(`${API}/api/v1/cms/admin/${editItem.id}`, editItem);
            } else {
                await axios.post(`${API}/api/v1/cms/admin/create`, editItem);
            }
            setShowEditor(false);
            fetchData();
        } catch (e: any) {
            alert(e.response?.data?.detail || 'Kaydetme hatası');
        }
        setSaving(false);
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Bu içeriği silmek istediğinize emin misiniz?')) return;
        try {
            await axios.delete(`${API}/api/v1/cms/admin/${id}`);
            fetchData();
        } catch (e) {
            alert('Silme hatası');
        }
    };

    const togglePublish = async (item: CMSItem) => {
        try {
            await axios.put(`${API}/api/v1/cms/admin/${item.id}`, { is_published: !item.is_published });
            fetchData();
        } catch (e) {
            alert('Güncelleme hatası');
        }
    };

    const toggleFeatured = async (item: CMSItem) => {
        try {
            await axios.put(`${API}/api/v1/cms/admin/${item.id}`, { is_featured: !item.is_featured });
            fetchData();
        } catch (e) {
            alert('Güncelleme hatası');
        }
    };

    const typeColor = (t: string) => {
        if (t === 'blog') return 'bg-blue-100 text-blue-700';
        if (t === 'impact') return 'bg-green-100 text-green-700';
        if (t === 'press') return 'bg-purple-100 text-purple-700';
        return 'bg-gray-100 text-gray-700';
    };

    const typeLabel = (t: string) => {
        if (t === 'blog') return 'Blog';
        if (t === 'impact') return 'Etki';
        if (t === 'press') return 'Basın';
        return t;
    };

    return (
        <div className="space-y-6">
          <div className="max-w-6xl mx-auto">

            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">CMS İçerik Yönetimi</h1>
                    <p className="text-sm text-gray-500 mt-1">Blog, Etki ve Basın içeriklerini yönetin</p>
                </div>
                <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium text-sm transition-colors">
                    <Plus className="w-4 h-4" /> Yeni İçerik
                </button>
            </div>

            {/* Stats */}
            {stats && (
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-6">
                    <StatCard value={stats.total} label="Toplam" color="gray" />
                    <StatCard value={stats.published} label="Yayında" color="green" />
                    <StatCard value={stats.drafts} label="Taslak" color="amber" />
                    <StatCard value={stats.blog} label="Blog" color="blue" />
                    <StatCard value={stats.impact} label="Etki" color="pink" />
                    <StatCard value={stats.press} label="Basın" color="purple" />
                </div>
            )}

            {/* Search & Filters */}
            <div className="bg-white rounded-2xl p-4 mb-6">
                <form onSubmit={handleSearch} className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        value={search} onChange={e => setSearch(e.target.value)}
                        placeholder="Başlık veya içerik ara..."
                        className="w-full pl-10 pr-4 py-3 bg-gray-50 rounded-xl text-sm text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-purple-500"
                    />
                </form>
                <div className="flex gap-2 flex-wrap items-center">
                    {TYPES.map(tp => (
                        <button key={tp.value} onClick={() => setTypeFilter(tp.value)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${typeFilter === tp.value ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                            {tp.label}
                        </button>
                    ))}
                    <div className="w-px h-5 bg-gray-200 mx-1" />
                    {[
                        { v: '', l: 'Tüm Durum' },
                        { v: 'true', l: 'Yayında' },
                        { v: 'false', l: 'Taslak' },
                    ].map(p => (
                        <button key={p.v} onClick={() => setPubFilter(p.v)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${pubFilter === p.v ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                            {p.l}
                        </button>
                    ))}
                    <span className="ml-auto text-xs text-gray-500">{items.length} sonuç</span>
                </div>
            </div>

            {/* Items Grid */}
            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
                </div>
            ) : items.length === 0 ? (
                <div className="bg-white rounded-2xl p-12 text-center">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Henüz içerik yok</p>
                    <button onClick={openCreate} className="mt-3 text-purple-600 text-sm font-medium">+ İlk içeriği oluştur</button>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map(item => (
                        <div key={item.id} className="bg-white rounded-2xl overflow-hidden hover:ring-2 hover:ring-purple-200 transition-all cursor-pointer"
                            onClick={() => openEdit(item)}>
                            {/* Cover Image */}
                            {item.cover_image ? (
                                <div className="h-36 bg-gray-100 overflow-hidden">
                                    <img src={item.cover_image} alt="" className="w-full h-full object-cover" onError={e => (e.currentTarget.style.display = 'none')} />
                                </div>
                            ) : (
                                <div className="h-20 bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center">
                                    <FileText className="w-8 h-8 text-purple-300" />
                                </div>
                            )}

                            <div className="p-4">
                                {/* Badges */}
                                <div className="flex items-center gap-1.5 mb-2">
                                    <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${typeColor(item.content_type)}`}>{typeLabel(item.content_type)}</span>
                                    {item.is_published
                                        ? <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700">Yayında</span>
                                        : <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">Taslak</span>}
                                    {item.is_featured && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />}
                                </div>

                                {/* Title */}
                                <h3 className="font-medium text-gray-900 text-sm line-clamp-2 mb-1">{item.title}</h3>
                                {item.excerpt && <p className="text-xs text-gray-500 line-clamp-2 mb-3">{item.excerpt}</p>}

                                {/* Meta */}
                                <div className="flex items-center gap-2 text-[10px] text-gray-400 mb-3">
                                    <span>{item.language?.toUpperCase()}</span>
                                    {item.category && <><span>·</span><span>{item.category}</span></>}
                                    <span>·</span>
                                    <span>{item.read_time} dk</span>
                                </div>

                                {/* Footer */}
                                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                                    <div className="flex items-center gap-3 text-xs text-gray-400">
                                        <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{item.view_count}</span>
                                        <span>{item.author}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <button onClick={(e) => { e.stopPropagation(); toggleFeatured(item); }}
                                            className={`p-1.5 rounded-xl transition-colors ${item.is_featured ? 'bg-amber-100 text-amber-600' : 'hover:bg-gray-100 text-gray-400'}`}>
                                            <Star className="w-3.5 h-3.5" />
                                        </button>
                                        <button onClick={(e) => { e.stopPropagation(); togglePublish(item); }}
                                            className={`p-1.5 rounded-xl transition-colors ${item.is_published ? 'bg-green-100 text-green-600' : 'hover:bg-gray-100 text-gray-400'}`}>
                                            {item.is_published ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                                        </button>
                                        <button onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                                            className="p-1.5 hover:bg-red-50 text-red-400 rounded-xl transition-colors">
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Editor Modal */}
            {showEditor && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl w-full max-w-3xl my-8">
                        <div className="flex items-center justify-between p-5 border-b">
                            <h2 className="text-lg font-bold text-gray-900">
                                {editItem.id ? 'İçerik Düzenle' : 'Yeni İçerik Oluştur'}
                            </h2>
                            <button onClick={() => setShowEditor(false)} className="p-2 hover:bg-gray-100 rounded-xl"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
                            {/* Row: Type + Language */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Tür</label>
                                    <select value={editItem.content_type} onChange={e => setEditItem({ ...editItem, content_type: e.target.value, category: 'General' })}
                                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-200">
                                        <option value="blog">Blog</option>
                                        <option value="impact">Etki</option>
                                        <option value="press">Basın</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Dil</label>
                                    <select value={editItem.language} onChange={e => setEditItem({ ...editItem, language: e.target.value })}
                                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-200">
                                        {LANGS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                                    </select>
                                </div>
                            </div>
                            {/* Title */}
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Başlık</label>
                                <input value={editItem.title || ''} onChange={e => setEditItem({ ...editItem, title: e.target.value })}
                                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-200"
                                    placeholder="İçerik başlığı..." />
                            </div>
                            {/* Slug */}
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Slug (URL)</label>
                                <input value={editItem.slug || ''} onChange={e => setEditItem({ ...editItem, slug: e.target.value })}
                                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-200"
                                    placeholder="otomatik-olusturulur" />
                            </div>
                            {/* Row: Category + Author + Read Time */}
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Kategori</label>
                                    <select value={editItem.category || 'General'} onChange={e => setEditItem({ ...editItem, category: e.target.value })}
                                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-200">
                                        {(CATEGORIES[editItem.content_type || 'blog'] || CATEGORIES.blog).map(c => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Yazar</label>
                                    <input value={editItem.author || ''} onChange={e => setEditItem({ ...editItem, author: e.target.value })}
                                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-200" />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Okuma (dk)</label>
                                    <input type="number" value={editItem.read_time || 5} onChange={e => setEditItem({ ...editItem, read_time: parseInt(e.target.value) || 5 })}
                                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-200" />
                                </div>
                            </div>
                            {/* Cover Image */}
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Kapak Görseli</label>
                                <div className="flex gap-2 mb-2">
                                    <button type="button" onClick={() => setImageMode('url')}
                                        className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-colors ${imageMode === 'url' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                                        <Globe className="w-3 h-3 inline mr-1" />URL
                                    </button>
                                    <button type="button" onClick={() => setImageMode('upload')}
                                        className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-colors ${imageMode === 'upload' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                                        <Upload className="w-3 h-3 inline mr-1" />Dosya Yükle
                                    </button>
                                </div>
                                {imageMode === 'url' ? (
                                    <input value={editItem.cover_image || ''} onChange={e => setEditItem({ ...editItem, cover_image: e.target.value })}
                                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-200"
                                        placeholder="https://..." />
                                ) : (
                                    <div className="flex items-center gap-3">
                                        <label className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors">
                                            <Upload className="w-4 h-4 text-gray-400" />
                                            <span className="text-sm text-gray-500">{uploading ? 'Yükleniyor...' : 'Görsel seç (max 5MB)'}</span>
                                            <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
                                        </label>
                                    </div>
                                )}
                                {editItem.cover_image && (
                                    <div className="mt-2 flex items-center gap-3">
                                        <img src={editItem.cover_image} alt="" className="h-16 w-24 object-cover rounded-xl border" onError={e => (e.currentTarget.style.display = 'none')} />
                                        <span className="text-xs text-gray-400 truncate flex-1">{editItem.cover_image}</span>
                                        <button type="button" onClick={() => setEditItem({ ...editItem, cover_image: '' })} className="text-red-400 hover:text-red-600 text-xs">Kaldır</button>
                                    </div>
                                )}
                            </div>
                            {/* Excerpt */}
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Özet</label>
                                <textarea value={editItem.excerpt || ''} onChange={e => setEditItem({ ...editItem, excerpt: e.target.value })}
                                    rows={2}
                                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-200 resize-none"
                                    placeholder="Kısa açıklama..." />
                            </div>
                            {/* Body */}
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">İçerik (Markdown / HTML)</label>
                                <textarea value={editItem.body || ''} onChange={e => setEditItem({ ...editItem, body: e.target.value })}
                                    rows={12}
                                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-200 resize-y"
                                    placeholder="İçerik metni..." />
                            </div>
                            {/* Tags + Meta */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Etiketler</label>
                                    <input value={editItem.tags || ''} onChange={e => setEditItem({ ...editItem, tags: e.target.value })}
                                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-200"
                                        placeholder="tag1, tag2, tag3" />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Sıralama</label>
                                    <input type="number" value={editItem.sort_order || 0} onChange={e => setEditItem({ ...editItem, sort_order: parseInt(e.target.value) || 0 })}
                                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-200" />
                                </div>
                            </div>
                            {/* Meta Description */}
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Meta Açıklama (SEO)</label>
                                <input value={editItem.meta_description || ''} onChange={e => setEditItem({ ...editItem, meta_description: e.target.value })}
                                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-200"
                                    placeholder="SEO açıklaması..." />
                            </div>
                            {/* Toggles */}
                            <div className="flex items-center gap-6">
                                <Toggle
                                    enabled={editItem.is_published || false}
                                    onChange={(v) => setEditItem({ ...editItem, is_published: v })}
                                    label="Yayınla"
                                />
                                <Toggle
                                    enabled={editItem.is_featured || false}
                                    onChange={(v) => setEditItem({ ...editItem, is_featured: v })}
                                    label="Öne Çıkar"
                                />
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-3 p-5 border-t">
                            <button onClick={() => setShowEditor(false)} className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl text-sm font-medium transition-colors">İptal</button>
                            <button onClick={handleSave} disabled={saving}
                                className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-xl text-sm font-medium transition-colors">
                                <Save className="w-4 h-4" /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

          </div>
        </div>
    );
}
